'use client';

import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { useFetch, useGlobalModal } from '@/app/hooks';
import { reportEvent } from '@/app/utils';
import { fileStateUpdaters } from '../_utils';
import type { FileOperationType } from '../_utils/file-operations-config';
import { favorsBatchFavorite, fetchDeleteFileOrFolder } from '../apis';
import type { FileItem } from '../types';

export interface UseFileOperationsOptions {
  onMoveDialogOpen?: (items: FileItem[]) => void;
  // 文件状态更新回调
  onFilesUpdate?: (updater: (files: FileItem[]) => FileItem[]) => void;
}

export function useFileOperations({ onMoveDialogOpen, onFilesUpdate }: UseFileOperationsOptions = {}) {
  const [isLoading, setIsLoading] = useState<FileOperationType | null>(null);
  const { open: openShareDialog } = useGlobalModal('shareDialog');
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');
  const t = useTranslations();

  const { runAsync: batchDeleteItems } = useFetch(
    (items: FileItem[]) =>
      fetchDeleteFileOrFolder({
        body: {
          items: items.map((item) => ({
            id: item.id,
            type: item.type,
          })),
        },
      }),
    {
      manual: true,
      onSuccess: ({ error, data }, [items]) => {
        const creationIds = items
          .map((item) => item.creationId)
          .filter(Boolean)
          .join(',');

        if (error) {
          if (error?.reason === 'FolderHasChildNodes') {
            openConfirmDialog({
              title: t('creations.warning.deleteFolderFailed'),
              description: t('creations.warning.deleteFolderFailedDescription'),
              primaryButtonText: t('common.dialog.gotIt'),
            });
          } else {
            toast({
              message: error?.message,
              type: 'error',
            });
          }
          reportEvent('select_click', {
            select_name: 'delete',
            select_amount: items.length,
            creation_id: creationIds,
            is_success: false,
          });
          return;
        }

        // 可能会有部分失败，需要提示用户
        if (data?.failed_ids?.length > 0) {
          openConfirmDialog({
            title: t('creations.warning.deletePartialFailed'),
            description: t('creations.warning.deletePartialFailedDescription'),
            primaryButtonText: t('common.dialog.gotIt'),
          });
        } else {
          toast({
            message: t('creations.operations.deleteSuccess'),
            type: 'success',
          });
        }

        // 直接更新本地状态，移除已删除的文件
        onFilesUpdate?.((files) => fileStateUpdaters.removeFiles(files, data?.success_ids));
        reportEvent('select_click', {
          select_name: 'delete',
          select_amount: items.length,
          creation_id: creationIds,
          is_success: true,
        });
      },
    },
  );

  const { runAsync: batchMarkItems } = useFetch(
    (items: FileItem[], isAllMarked: boolean) =>
      favorsBatchFavorite({
        body: {
          favorites: items.map((item) => ({
            obj_id: item.type === 'creation' ? item.creationId || item.id : item.id,
            obj_type: item.type,
          })),
          is_favorite: !isAllMarked, // 如果全部已收藏则取消收藏，否则收藏
        },
      }),
    {
      manual: true,
      onSuccess: ({ error }, [items, isAllMarked]) => {
        const creationIds = items.map((item) => item.creationId).join(',');

        if (error) {
          toast({
            type: 'warning',
            message: isAllMarked
              ? t('creations.batchOperation.unfavoriteFailed')
              : t('creations.batchOperation.favoriteFailed'),
          });
          reportEvent('select_click', {
            select_name: isAllMarked ? 'unfavorite' : 'favorite',
            select_amount: items.length,
            creation_id: creationIds,
            is_success: false,
          });
          return;
        }

        const newFavoriteStatus = !isAllMarked;
        toast({
          type: 'success',
          message: newFavoriteStatus
            ? t('creations.batchOperation.favoriteSuccess')
            : t('creations.batchOperation.unfavoriteSuccess'),
        });

        // 直接更新本地状态，更新收藏状态
        onFilesUpdate?.((files) => fileStateUpdaters.updateFavoriteStatus(files, items, newFavoriteStatus));

        reportEvent('select_click', {
          select_name: isAllMarked ? 'unfavorite' : 'favorite',
          select_amount: items.length,
          creation_id: creationIds,
          is_success: true,
        });
      },
    },
  );

  // 分享文件
  const handleShare = async (item: FileItem) => {
    openShareDialog({
      creationId: item.creationId || '',
      creationType: item.creationType,
      taskId: item.taskId,
      reportParams: {
        // 文件列表只有生成物类型，没有任务 settings，所以 model_version / resolution 等维度拿不到
        content_type: item.creationType === 'image' || item.creationType === 'audio' ? item.creationType : 'video',
      },
    });
  };

  // 移动文件
  const handleMove = (items: FileItem[] | FileItem) => {
    const itemsArray = Array.isArray(items) ? items : [items];
    onMoveDialogOpen?.(itemsArray);
  };

  // 移动操作完成后的回调（供外部调用）
  const handleMoveComplete = (movedItemIds: string[]) => {
    // 直接更新本地状态，从当前列表中移除已移动的文件
    onFilesUpdate?.((files) => fileStateUpdaters.moveFiles(files, movedItemIds));
  };

  // 删除文件
  const handleDelete = async (items: FileItem[] | FileItem) => {
    // 如果文件夹有子文件夹，不允许删除
    const hasSubFolder = Array.isArray(items) ? items.some((item) => item.hasSubFolder) : items.hasSubFolder;
    if (hasSubFolder) {
      openConfirmDialog({
        title: t('creations.warning.deleteFolderFailed'),
        description: t('creations.warning.deleteFolderFailedDescription'),
        primaryButtonText: t('common.dialog.gotIt'),
      });
      return;
    }

    const isIncludeFolder = Array.isArray(items)
      ? items.some((item) => item.type === 'folder')
      : items.type === 'folder';
    openConfirmDialog({
      title: t('popup.dialog.delete.title'),
      description: isIncludeFolder
        ? t('creations.operations.deleteConfirmDialog.contentWithFolder')
        : t('popup.dialog.delete.content'),
      onPrimaryAction: async () => {
        setIsLoading('delete');
        await batchDeleteItems(Array.isArray(items) ? items : [items]);
        setIsLoading(null);
      },
      primaryButtonText: t('common.dialog.delete'),
      secondaryButtonText: t('common.dialog.cancel'),
    });
  };

  // 标记文件
  const handleMark = async (items: FileItem[] | FileItem) => {
    const itemsArray = Array.isArray(items) ? items : [items];
    // 检查是否所有选中的文件都已收藏
    const isAllMarked = itemsArray.length > 0 && itemsArray.every((item) => item.is_favorite);

    setIsLoading('mark');
    await batchMarkItems(itemsArray, isAllMarked);
    setIsLoading(null);
  };

  // 统一的操作处理器
  const handleOperation = async (operation: FileOperationType, items: FileItem[] | FileItem) => {
    switch (operation) {
      case 'share':
        return handleShare(Array.isArray(items) ? items[0] : items);
      case 'move':
        return handleMove(items);
      case 'delete':
        return handleDelete(items);
      case 'mark':
        return handleMark(items);
      default:
        console.warn(`Unknown operation: ${operation}`);
    }
  };

  return {
    isLoading,
    handleOperation,
    handleShare,
    handleMove,
    handleDelete,
    handleMark,
    handleMoveComplete,
  };
}
